import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Loader2, Star, CheckCircle2, ExternalLink, AlertTriangle } from 'lucide-react';

// Public, unauthenticated feedback landing page reached from the survey link in
// the resolution email or text. Nothing here needs a login, so it has to work
// on a phone with no session at all.
//
// A 4 or 5 is recorded straight away and points the customer at Google; a 1 to 3
// opens a comment box, and the backend reopens the case for a supervisor.

const GOOGLE_REVIEW_URL = 'https://g.page/r/CSnwEYnMDB_5EBM/review';

export default function CaseFeedback() {
  const { referenceCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [finalRating, setFinalRating] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await base44.functions.invoke('caseFeedback', {
          action: 'load',
          referenceCode,
        });
        if (cancelled) return;
        if (res.data?.error) throw new Error(res.data.error);
        setInfo(res.data.case);
        if (res.data.case?.alreadySubmitted) {
          setFinalRating(Number(res.data.case.rating) || 0);
          setDone(true);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.response?.data?.error || e.message || 'Something went wrong.');
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [referenceCode]);

  const submit = async (value, comment) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await base44.functions.invoke('caseFeedback', {
        action: 'submit',
        referenceCode,
        rating: value,
        comments: comment,
      });
      if (res.data?.error) throw new Error(res.data.error);
      setFinalRating(value);
      setDone(true);
    } catch (e) {
      setSubmitError(e?.response?.data?.error || e.message || 'Could not send your feedback.');
    }
    setSubmitting(false);
  };

  // A good score needs nothing more from the customer, so it is recorded as
  // soon as the star is tapped. A poor score earns the comment box.
  const choose = (value) => {
    setRating(value);
    setSubmitError('');
    if (value >= 4) submit(value, '');
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-primary font-black text-lg tracking-tight uppercase">MOTION REALITY</p>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mt-1">
            SIM RACING · BROMBOROUGH, WIRRAL
          </p>
        </div>

        <div className="border border-white/10 bg-[#111115]">
          <div className="h-[3px] bg-primary" />
          {children}
        </div>

        <p className="text-center text-white/20 text-[10px] mt-6 leading-relaxed">
          Motion Reality Sim Racing Ltd · Unit 9, Croft Business Park, Bromborough, Wirral CH62 3RA
        </p>
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return shell(
      <div className="p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-400/60 mx-auto mb-4" />
        <h1 className="text-white font-black text-lg uppercase">We couldn't open that link</h1>
        <p className="text-white/50 text-sm mt-3 leading-relaxed">{loadError}</p>
        <p className="text-white/30 text-xs mt-4">
          If you think this is a mistake, just reply to our email or call 0151 528 3001.
        </p>
      </div>
    );
  }

  const firstName = (info?.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const ref = info?.reference_code || referenceCode;

  if (done) {
    const high = finalRating >= 4;
    const low = finalRating > 0 && finalRating <= 3;

    return shell(
      <div className="p-8 text-center">
        <CheckCircle2 className={`w-10 h-10 mx-auto mb-4 ${low ? 'text-amber-400/70' : 'text-green-400/70'}`} />
        <h1 className="text-white font-black text-xl uppercase leading-tight">
          {high ? 'Thank you!' : low ? "We're on it" : 'Feedback already received'}
        </h1>
        <p className="text-white/50 text-sm mt-3 leading-relaxed">
          {high
            ? `Really glad we got enquiry ${ref} right. If you have a moment, a Google review helps other drivers find us.`
            : low
              ? 'Thank you for telling us. Your comments have gone straight to a supervisor, who will look at enquiry ' +
                `${ref} personally.`
              : `We already have your feedback for enquiry ${ref} — thank you.`}
        </p>

        {high && (
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
          >
            LEAVE A GOOGLE REVIEW <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}

        <div className="flex items-center justify-center gap-1 mt-6">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={`w-4 h-4 ${
                finalRating >= n ? 'text-primary fill-primary' : 'text-white/10'
              }`}
            />
          ))}
        </div>
      </div>
    );
  }

  return shell(
    <div className="p-6">
      <p className="text-white/30 text-[10px] font-bold tracking-widest">ENQUIRY {ref}</p>
      <h1 className="text-white font-black text-2xl uppercase mt-2 leading-tight">How did we do?</h1>
      <p className="text-white/50 text-sm mt-3 leading-relaxed">
        Hi {firstName} — thanks for getting in touch. Your answer goes straight to the team that
        handled your enquiry.
      </p>

      <div
        className="flex items-center justify-center gap-2 mt-8"
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => choose(n)}
            onMouseEnter={() => setHover(n)}
            disabled={submitting}
            aria-label={`Rate ${n} out of 5`}
            className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
          >
            <Star
              className={`w-9 h-9 transition-colors ${
                (hover || rating) >= n ? 'text-primary fill-primary' : 'text-white/15'
              }`}
            />
          </button>
        ))}
      </div>
      <p className="text-center text-white/30 text-[10px] font-bold tracking-widest mt-3">
        {rating === 0 ? 'TAP A STAR TO RATE' : `${rating} OUT OF 5`}
      </p>

      {rating > 0 && rating <= 3 && (
        <div className="mt-8 space-y-4">
          <p className="text-white/60 text-sm leading-relaxed">
            We're sorry we didn't get this right. Tell us what went wrong and a supervisor will pick
            it up personally.
          </p>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={5}
            placeholder="What could we have done better?"
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 outline-none focus:border-primary/60 transition-colors resize-none leading-relaxed placeholder:text-white/20"
          />
          {submitError && <p className="text-red-400 text-xs">{submitError}</p>}
          <button
            type="button"
            onClick={() => submit(rating, comments)}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'SENDING…' : 'SEND FEEDBACK'}
          </button>
        </div>
      )}

      {submitting && rating >= 4 && (
        <div className="flex justify-center mt-8">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      )}
      {submitError && rating >= 4 && (
        <p className="text-red-400 text-xs mt-4 text-center">{submitError}</p>
      )}
    </div>
  );
}